import { z } from "zod";

/* ==========================================================================
   Modello del documento diagramma (DiagramDoc).
   È la fonte di verità: il client lo renderizza e lo edita, il server lo
   memorizza come JSON e ne versiona gli snapshot. Lo schema Zod serve a
   validare in ingresso/uscita da entrambi i lati.

   Deriva 1:1 dalle strutture PARTICIPANTS / DOCS / FLOW di flusso-urgenze.html
   (10 tipi di step verificati). Rispetto all'originale, il colore dei
   partecipanti è reso esplicito per tema (dark/light) così l'attore è
   completamente auto-descrittivo ed editabile.
   ========================================================================== */

const hexColor = z
  .string()
  .regex(/^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/, "colore esadecimale non valido");

/** id "slug" usato come chiave (partecipanti, documenti, fasi, sync). */
const idSchema = z
  .string()
  .min(1)
  .regex(/^[a-z0-9][a-z0-9_-]*$/i, "id non valido (usa lettere/cifre/-/_)");

/* ---------------------------------- attori ---------------------------------- */

export const ParticipantKind = z.enum(["participant", "actor"]);

export const ParticipantSchema = z.object({
  id: idSchema,
  name: z.string().min(1),
  kind: ParticipantKind,
  /** attore temporaneo (create/destroy): es. Committente, Addetto licenze. */
  temp: z.boolean().optional(),
  /** nome icona Lucide incorporata nel motore (vedi engine/icons). */
  icon: z.string().min(1),
  /** colore per tema; il motore usa colors[theme] a runtime. */
  colors: z.object({ dark: hexColor, light: hexColor }),
});

/* -------------------------------- documenti --------------------------------- */

export const DocSchema = z.object({
  id: idSchema,
  short: z.string().min(1),
  label: z.string().min(1),
  /** accorpa più voci sotto un'unica etichetta cliccabile (es. "All. 18"). */
  group: z.string().optional(),
});

/* ---------------------------------- flow ------------------------------------ */

export const DeadlineSchema = z.object({
  short: z.string().min(1),
  label: z.string().min(1),
});

export const BlockKind = z.enum(["alt", "opt", "loop"]);

const PhaseStep = z.object({
  t: z.literal("phase"),
  id: idSchema,
  title: z.string().min(1),
  sub: z.string().optional(),
});

const NoteStep = z.object({
  t: z.literal("note"),
  over: idSchema,
  text: z.string(),
});

const MsgStep = z.object({
  t: z.literal("msg"),
  from: idSchema,
  to: idSchema,
  text: z.string(),
  /** chiave di raggruppamento "contemporanei": msg consecutivi con lo
      stesso valore collassano su un'unica riga. */
  sync: z.string().min(1).optional(),
  docs: z.array(idSchema).optional(),
  deadline: DeadlineSchema.optional(),
});

const ActStep = z.object({ t: z.literal("act"), a: idSchema });
const DeactStep = z.object({ t: z.literal("deact"), a: idSchema });
const CreateStep = z.object({
  t: z.literal("create"),
  a: idSchema,
  inline: z.boolean().optional(),
});
const DestroyStep = z.object({ t: z.literal("destroy"), a: idSchema });

const BlockStep = z.object({
  t: z.literal("block"),
  kind: BlockKind,
  cond: z.string(),
});
const ElseStep = z.object({ t: z.literal("else"), cond: z.string() });
const EndStep = z.object({ t: z.literal("end") });

export const FlowStepSchema = z.discriminatedUnion("t", [
  PhaseStep,
  NoteStep,
  MsgStep,
  ActStep,
  DeactStep,
  CreateStep,
  DestroyStep,
  BlockStep,
  ElseStep,
  EndStep,
]);

/* -------------------------------- documento --------------------------------- */

export const DiagramMetaSchema = z.object({
  title: z.string().min(1),
  lang: z.string().default("it"),
});

export const DiagramDocSchema = z.object({
  meta: DiagramMetaSchema,
  participants: z.array(ParticipantSchema).min(1),
  docs: z.array(DocSchema),
  flow: z.array(FlowStepSchema),
});

/* --------------------------------- tipi TS ---------------------------------- */

export type Participant = z.infer<typeof ParticipantSchema>;
export type Doc = z.infer<typeof DocSchema>;
export type Deadline = z.infer<typeof DeadlineSchema>;
export type FlowStep = z.infer<typeof FlowStepSchema>;
export type FlowStepType = FlowStep["t"];
export type DiagramMeta = z.infer<typeof DiagramMetaSchema>;
export type DiagramDoc = z.infer<typeof DiagramDocSchema>;

/* ==========================================================================
   Validazione semantica oltre alla forma: riferimenti agli id e bilanciamento
   dei blocchi (block/else/end). Zod garantisce la struttura; questa funzione
   garantisce la coerenza referenziale che il motore assume.
   ========================================================================== */

export interface DocIssue {
  /** indice dello step in flow (se applicabile). */
  index?: number;
  kind:
    | "unknown-participant"
    | "unknown-doc"
    | "unbalanced-block"
    | "orphan-else"
    | "orphan-end"
    | "duplicate-id";
  message: string;
}

export function validateDiagram(doc: DiagramDoc): DocIssue[] {
  const issues: DocIssue[] = [];
  const partIds = new Set<string>();
  for (const p of doc.participants) {
    if (partIds.has(p.id))
      issues.push({ kind: "duplicate-id", message: `partecipante duplicato: ${p.id}` });
    partIds.add(p.id);
  }
  const docIds = new Set<string>();
  for (const d of doc.docs) {
    if (docIds.has(d.id))
      issues.push({ kind: "duplicate-id", message: `documento duplicato: ${d.id}` });
    docIds.add(d.id);
  }

  const needPart = (id: string, index: number, field: string) => {
    if (!partIds.has(id))
      issues.push({
        index,
        kind: "unknown-participant",
        message: `${field} "${id}" non è un partecipante noto`,
      });
  };

  let depth = 0; // profondità blocchi aperti
  doc.flow.forEach((step, index) => {
    switch (step.t) {
      case "msg":
        needPart(step.from, index, "from");
        needPart(step.to, index, "to");
        for (const d of step.docs ?? [])
          if (!docIds.has(d))
            issues.push({ index, kind: "unknown-doc", message: `documento "${d}" non noto` });
        break;
      case "note":
        needPart(step.over, index, "over");
        break;
      case "act":
      case "deact":
      case "create":
      case "destroy":
        needPart(step.a, index, "a");
        break;
      case "block":
        depth++;
        break;
      case "else":
        if (depth === 0)
          issues.push({ index, kind: "orphan-else", message: "else senza block aperto" });
        break;
      case "end":
        if (depth === 0)
          issues.push({ index, kind: "orphan-end", message: "end senza block aperto" });
        else depth--;
        break;
      case "phase":
        break;
    }
  });
  if (depth > 0)
    issues.push({
      kind: "unbalanced-block",
      message: `${depth} blocco/i non chiuso/i (manca 'end')`,
    });

  return issues;
}

/** Parsa e valida forma + semantica. Lancia con messaggio leggibile se invalido. */
export function parseDiagram(input: unknown): DiagramDoc {
  const doc = DiagramDocSchema.parse(input);
  const issues = validateDiagram(doc);
  if (issues.length)
    throw new Error("Documento incoerente:\n" + issues.map((i) => "· " + i.message).join("\n"));
  return doc;
}
